import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Modal, Form, Alert, Spinner, Badge, Nav, Tab, Card } from 'react-bootstrap';
import { notifications } from '@mantine/notifications';

import '../admin/AccountStyles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004';

const RateManagementScreen = () => {
  const [ratePlans, setRatePlans] = useState([]);
  const [barHeaders, setBarHeaders] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRatePlan, setEditingRatePlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    bar_rate_id: '',
    market_id: '',
    source_id: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch each API separately to handle individual failures
      const fetchRatePlans = async () => {
        try {
          console.log('Fetching rate plans from:', `${API_URL}/api/rates`);
          const response = await fetch(`${API_URL}/api/rates`);
          console.log('Rate plans response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.log('Rate plans error response:', errorText);
            throw new Error(`Failed to fetch rate plans: ${response.status} - ${errorText}`);
          }
          const data = await response.json();
          console.log('Rate plans data received:', data);
          return data;
        } catch (error) {
          console.error('Error fetching rate plans:', error);
          return [];
        }
      };

      const fetchBarHeaders = async () => {
        try {
          console.log('Fetching BAR headers from:', `${API_URL}/api/rates-bar-header`);
          const response = await fetch(`${API_URL}/api/rates-bar-header`);
          console.log('BAR headers response status:', response.status);
          console.log('BAR headers response ok:', response.ok);
          if (!response.ok) {
            const errorText = await response.text();
            console.log('BAR headers error response:', errorText);
            throw new Error(`Failed to fetch BAR headers: ${response.status} - ${errorText}`);
          }
          const data = await response.json();
          console.log('BAR headers data received:', data);
          return data;
        } catch (error) {
          console.error('Error fetching BAR headers:', error);
          return [];
        }
      };

      const fetchMarkets = async () => {
        try {
          console.log('Fetching markets from:', `${API_URL}/api/markets`);
          const response = await fetch(`${API_URL}/api/markets`);
          console.log('Markets response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.log('Markets error response:', errorText);
            throw new Error(`Failed to fetch markets: ${response.status} - ${errorText}`);
          }
          const data = await response.json();
          console.log('Markets data received:', data);
          return data;
        } catch (error) {
          console.error('Error fetching markets:', error);
          return [];
        }
      };

      const fetchSources = async () => {
        try {
          console.log('Fetching sources from:', `${API_URL}/api/sources`);
          const response = await fetch(`${API_URL}/api/sources`);
          console.log('Sources response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.log('Sources error response:', errorText);
            throw new Error(`Failed to fetch sources: ${response.status} - ${errorText}`);
          }
          const data = await response.json();
          console.log('Sources data received:', data);
          return data;
        } catch (error) {
          console.error('Error fetching sources:', error);
          return [];
        }
      };

      const [ratePlansData, barHeadersData, marketsData, sourcesData] = await Promise.all([
        fetchRatePlans(),
        fetchBarHeaders(),
        fetchMarkets(),
        fetchSources()
      ]);

      setRatePlans(ratePlansData);
      setBarHeaders(barHeadersData);
      setMarkets(marketsData);
      setSources(sourcesData);

      // Debug logging
      console.log('BAR Headers loaded:', barHeadersData);
      console.log('Markets loaded:', marketsData);
      console.log('Sources loaded:', sourcesData);

      // Show warning if some data couldn't be loaded
      const failedFetches = [];
      if (ratePlansData.length === 0) failedFetches.push('rate plans');
      if (barHeadersData.length === 0) failedFetches.push('BAR headers');
      if (marketsData.length === 0) failedFetches.push('markets');
      if (sourcesData.length === 0) failedFetches.push('sources');

      if (failedFetches.length > 0) {
        notifications.show({
          title: 'Warning',
          message: `Could not load: ${failedFetches.join(', ')}`,
          color: 'orange',
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch data',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRatePlan(null);
    setFormData({
      title: '',
      description: '',
      bar_rate_id: '',
      market_id: '',
      source_id: '',
      active: true
    });
    setModalOpen(true);
  };

  const handleEdit = (ratePlan) => {
    console.log('Editing rate plan:', ratePlan); // Debug log
    setEditingRatePlan(ratePlan);
    setFormData({
      title: ratePlan.title || '',
      description: ratePlan.description || '',
      bar_rate_id: ratePlan.bar_rate_id?.toString() || '',
      market_id: ratePlan.market_id?.toString() || '',
      source_id: ratePlan.source_id?.toString() || '',
      active: ratePlan.active || false
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.bar_rate_id) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please fill in rate code and select BAR rate',
        color: 'orange',
      });
      return;
    }

    try {
      const url = editingRatePlan 
        ? `${API_URL}/api/rates/${editingRatePlan.id}`
        : `${API_URL}/api/rates`;
      
      const method = editingRatePlan ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to save rate plan');
      
      const savedRatePlan = await response.json();
      
      if (editingRatePlan) {
        setRatePlans(ratePlans.map(rp => rp.id === editingRatePlan.id ? savedRatePlan : rp));
        notifications.show({
          title: 'Rate Plan Updated',
          message: `Rate plan "${savedRatePlan.title}" updated successfully`,
          color: 'green',
        });
      } else {
        setRatePlans([...ratePlans, savedRatePlan]);
        notifications.show({
          title: 'Rate Plan Created',
          message: `Rate plan "${savedRatePlan.title}" created successfully`,
          color: 'green',
        });
      }
      
      // Refresh all data to ensure dropdown data is available for display
      await fetchData();
      
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving rate plan:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save rate plan',
        color: 'red',
      });
    }
  };

  const handleDelete = async (ratePlan) => {
    if (!window.confirm(`Are you sure you want to delete rate plan "${ratePlan.title}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/rates/${ratePlan.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete rate plan');
      
      setRatePlans(ratePlans.filter(rp => rp.id !== ratePlan.id));
      notifications.show({
        title: 'Rate Plan Deleted',
        message: `Rate plan "${ratePlan.title}" deleted successfully`,
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting rate plan:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete rate plan',
        color: 'red',
      });
    }
  };

  // Helper functions to get names from IDs
  const getBarHeaderName = (barRateId) => {
    const header = barHeaders.find(h => h.id === parseInt(barRateId));
    return header ? header.bar : 'N/A';
  };

  const getMarketName = (marketId) => {
    const market = markets.find(m => m.id === parseInt(marketId));
    return market ? market.name : 'N/A';
  };

  const getSourceName = (sourceId) => {
    const source = sources.find(s => s.id === parseInt(sourceId));
    return source ? source.name : 'N/A';
  };

  const filteredRatePlans = ratePlans.filter(ratePlan => {
    const title = ratePlan.title || '';
    const description = ratePlan.description || '';
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = title.toLowerCase().includes(searchLower) ||
                         description.toLowerCase().includes(searchLower);
    
    if (activeTab === 'active') return matchesSearch && ratePlan.active;
    if (activeTab === 'inactive') return matchesSearch && !ratePlan.active;
    return matchesSearch;
  });

  return (
    <Container fluid className="account-screen p-0">
      <Row className="g-0">
        {/* Side Menu */}
        <Col xs={2} className="side-menu-container">
          <div className="side-menu">
            <div className="side-menu-section">
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => setSearchTerm('')}
              >
                Find
              </Button>
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={handleCreate}
              >
                Add
              </Button>
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => console.log('Change clicked')}
                disabled={true}
              >
                Change
              </Button>
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => console.log('Delete clicked')}
                disabled={true}
              >
                Delete
              </Button>
              
              {/* Line break */}
              <hr style={{ borderColor: '#bdc3c7', margin: '1rem 0' }} />
              
              {/* Import/Export buttons */}
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => console.log('Import clicked')}
              >
                Import
              </Button>
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => console.log('Export clicked')}
              >
                Export
              </Button>
              <Button 
                variant="outline-light" 
                className="side-menu-btn w-100 mb-2"
                onClick={() => console.log('Backup clicked')}
              >
                Backup
              </Button>
            </div>
          </div>
        </Col>

        {/* Main Content */}
        <Col xs={10} className="main-content">
          <Row className="align-items-center mb-3">
            <Col>
              <h1>Rate Management</h1>
            </Col>
            <Col xs="auto">
              <Button variant="primary" onClick={handleCreate}>
                Create Rate Plan
              </Button>
            </Col>
          </Row>

          {/* Debug info */}
          <div className="mb-3">
            <small className="text-muted">
              Debug: BAR Headers count: {barHeaders.length}, Markets count: {markets.length}, Sources count: {sources.length}
            </small>
          </div>

          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col xs={6}>
                  <Form.Control
                    type="text"
                    placeholder="Search rate plans..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 300 }}
                  />
                </Col>
                <Col xs={6}>
                  <Nav variant="tabs" defaultActiveKey={activeTab} onSelect={setActiveTab}>
                    <Nav.Item>
                      <Nav.Link eventKey="all">All</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="active">Active</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="inactive">Inactive</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : filteredRatePlans.length > 0 ? (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Rate Code</th>
                        <th>Description</th>
                        <th>BAR Rate</th>
                        <th>Market</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRatePlans.map((ratePlan) => (
                        <tr key={ratePlan.id}>
                          <td>
                            <strong>{ratePlan.title || 'N/A'}</strong>
                          </td>
                          <td>
                            <small className="text-muted">{ratePlan.description || 'N/A'}</small>
                          </td>
                          <td>
                            <small>{getBarHeaderName(ratePlan.bar_rate_id)}</small>
                          </td>
                          <td>
                            <small>{getMarketName(ratePlan.market_id)}</small>
                          </td>
                          <td>
                            <small>{getSourceName(ratePlan.source_id)}</small>
                          </td>
                          <td>
                            <Badge bg={ratePlan.active ? 'success' : 'danger'}>
                              {ratePlan.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEdit(ratePlan)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(ratePlan)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="warning">
                  <Alert.Heading>No Rate Plans Found</Alert.Heading>
                  {searchTerm ? 'No rate plans match your search criteria.' : 'No rate plans have been created yet.'}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Create/Edit Modal */}
          <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>{editingRatePlan ? 'Edit Rate Plan' : 'Create Rate Plan'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rate Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter rate code (e.g., STD001)"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>BAR Rate</Form.Label>
                      <Form.Select
                        value={formData.bar_rate_id}
                        onChange={(e) => setFormData({ ...formData, bar_rate_id: e.target.value })}
                        required
                      >
                        <option value="">Select BAR rate</option>
                        {barHeaders.map(header => {
                          console.log('Mapping BAR header:', header);
                          return (
                            <option key={header.id} value={header.id.toString()}>
                              {header.bar}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter rate plan description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Default Market Code</Form.Label>
                      <Form.Select
                        value={formData.market_id}
                        onChange={(e) => setFormData({ ...formData, market_id: e.target.value })}
                      >
                        <option value="">Select market</option>
                        {markets.map(market => (
                          <option key={market.id} value={market.id.toString()}>
                            {market.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Default Source of Business</Form.Label>
                      <Form.Select
                        value={formData.source_id}
                        onChange={(e) => setFormData({ ...formData, source_id: e.target.value })}
                      >
                        <option value="">Select source</option>
                        {sources.map(source => (
                          <option key={source.id} value={source.id.toString()}>
                            {source.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                {editingRatePlan ? 'Update' : 'Create'} Rate Plan
              </Button>
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default RateManagementScreen;
